export type Reference = string | { _id?: string; id?: string } | null | undefined;
export type GroupCourse = {
  _id: string;
  name?: string;
  isRemoved?: boolean;
  data?: { course?: Reference; lessonTemplate?: Reference; courseCategory?: Reference; teacher?: Reference };
  users?: { user?: Reference; paymentTransactions?: any[]; remainingPaidLessons?: number | null; totalPaidAmount?: number | null }[];
};
export type CourseEntry = {
  data?: { _id?: string; [key: string]: any } | string;
  lessonTemplate?: Reference;
  courseCategory?: Reference;
  teacher?: Reference;
  [key: string]: any;
};
export type LearningContext = {
  userId: string;
  courseId: string;
  key: string;
  mode: 'individual' | 'group';
  group?: GroupCourse;
};
export const refId = (value: Reference): string => typeof value === 'string' ? value : String(value?._id ?? value?.id ?? '');
export const courseIdOf = (entry: CourseEntry): string => refId(entry.data);
export const entryKey = (entry: CourseEntry): string =>
  ['individual', courseIdOf(entry), refId(entry.lessonTemplate), refId(entry.courseCategory), refId(entry.teacher)].join(':');
export const studentGroups = (groups: GroupCourse[], userId: string): GroupCourse[] =>
  groups.filter(group => !group.isRemoved && group.users?.some(member => refId(member.user) === userId));

export function catalogEntries(entries: CourseEntry[], groups: GroupCourse[], userId: string): CourseEntry[] {
  const ownedGroups = studentGroups(groups, userId);
  const result: CourseEntry[] = [];
  const seen = new Set<string>();
  for (const entry of entries) {
    const courseId = courseIdOf(entry);
    if (!courseId) continue;
    // A record already using the group's template is represented by the group card below.
    if (ownedGroups.some(group => refId(group.data?.course) === courseId && refId(group.data?.lessonTemplate) &&
      refId(group.data?.lessonTemplate) === refId(entry.lessonTemplate))) continue;
    const key = entryKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ ...entry, learningContextKey: key, learningMode: 'individual', groupLearning: false });
  }
  for (const group of ownedGroups) {
    const courseId = refId(group.data?.course);
    const base = entries.find(entry => courseIdOf(entry) === courseId);
    if (!base || typeof base.data !== 'object' || !base.data) continue;
    const member = group.users?.find(item => refId(item.user) === userId);
    const key = `group:${group._id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ ...base, _id: key, learningContextKey: key, learningMode: 'group',
      learningGroupId: group._id, learningGroup: group, groupLearning: true,
      paymentTransactions: member?.paymentTransactions ?? [], remainingPaidLessons: member?.remainingPaidLessons ?? 0,
      totalPaidAmount: member?.totalPaidAmount ?? 0,
      courseCategory: refId(group.data?.courseCategory) || null,
      lessonTemplate: refId(group.data?.lessonTemplate) || null,
      teacher: refId(base.teacher) === refId(group.data?.teacher) ? base.teacher : group.data?.teacher ?? null });
  }
  return result;
}

export function effectiveUserCourses(user: any, context: LearningContext | null | undefined): any {
  if (!user || user.role !== 'STUDENT' || !context || String(user._id) !== context.userId) return user;
  const entries: CourseEntry[] = user.courses ?? [];
  const matching = entries.filter(entry => courseIdOf(entry) === context.courseId);
  if (!matching.length) return user;
  let selected: CourseEntry | undefined;
  if (context.mode === 'group') {
    const group = context.group;
    if (!group || !studentGroups([group], context.userId).length || refId(group.data?.course) !== context.courseId) return user;
    const member = group.users?.find(item => refId(item.user) === context.userId);
    selected = { ...matching[0], paymentTransactions: member?.paymentTransactions ?? [],
      remainingPaidLessons: member?.remainingPaidLessons ?? 0, totalPaidAmount: member?.totalPaidAmount ?? 0, courseCategory: refId(group.data?.courseCategory) || null,
      lessonTemplate: refId(group.data?.lessonTemplate) || null, teacher: refId(matching[0].teacher) === refId(group.data?.teacher) ? matching[0].teacher : group.data?.teacher ?? null };
  } else selected = matching.find(entry => entryKey(entry) === context.key);
  if (!selected) return user;
  // Existing hooks use courses.find(courseId): expose exactly the selected learning context.
  return { ...user, courses: [selected, ...entries.filter(entry => courseIdOf(entry) !== context.courseId)] };
}
